import React, { useEffect, useState } from "react";
import "./MyOrganization.scss";

import { FaPen, FaTrash } from "react-icons/fa";

import { Link, useNavigate } from "react-router-dom";

import ROUTES from "../../../constants/routes";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../components/CustomButton/CustomButton";
import { addBranch, deleteBranch, updateBranch } from "../../../api/branches";
import {
  clearBranches,
  updateActiveBranch,
  updateBranches,
} from "../../../redux/features/branch-slice";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";
import { useForm } from "react-hook-form";
import InputField from "../../../components/InputField/InputField";
import { updateOpenSubscriptionAd } from "../../../redux/features/subscription-slice";
import { useViewPortDetector } from "../../../helpers/viewPortDetector";
import useUserIsAllowed from "../../../helpers/userAuthorized";
import { storage } from "../../../helpers/storage";

const IMAGE_URL = process.env.REACT_APP_IMAGE_URL;

const MyOrganization = () => {
  const { currentViewPort } = useViewPortDetector();
  const dispatch = useDispatch();

  const organization = useSelector((state) => state.organizationReducer.value);
  const { branches, id: branch_id } = useSelector(
    (state) => state.branchReducer.value
  );

  const navigate = useNavigate();

  const [openDelete, setOpenDelete] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);

  const [selectedBranch, setSelectedBranch] = useState(null);

  const [deleting, setDeleting] = useState(false);

  const userIsAllowedToViewOrganization = useUserIsAllowed("administrator");

  useEffect(() => {
    if (!userIsAllowedToViewOrganization) {
      toast.dismiss();
      toast.info("User unauthorized to use this page!");
      navigate(ROUTES.home.path);
    }
  }, [userIsAllowedToViewOrganization]);

  const handleDeleteBranch = () => {
    if (selectedBranch?.id && organization?.id) {
      setDeleting(true);
      deleteBranch(organization?.id, selectedBranch?.id)
        .then((res) => {
          toast.dismiss();
          toast.success("Branch deleted successfully.");
          setOpenDelete(false);

          const newBranches = branches?.filter(
            (el) => el.id !== selectedBranch.id
          );
          storage.set("branches", newBranches);
          dispatch(updateBranches({ branches: newBranches }));
          if (branch_id === selectedBranch.id) {
            if (newBranches?.length > 0) {
              storage.set("activeBranch", JSON.stringify(newBranches[0]));
              dispatch(updateActiveBranch(newBranches[0]));
            } else {
              storage.remove("branches");
              storage.remove("activeBranch");
              dispatch(clearBranches());
            }
          }

          setSelectedBranch(null);
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            if (err?.response?.data?.message === "User not authorized!") {
              toast.dismiss();
              toast.error(
                "You do not have sufficient permissions to perform this action. Please contact your administrator."
              );
            } else {
              toast.dismiss();
              toast.error(`${err?.response?.data?.message}`);
            }
          } else {
            toast.dismiss();
            toast.error("Error while deleting branch!");
          }
        })
        .finally(() => {
          setDeleting(false);
        });
    }
  };

  return (
    <>
      <div className="page-with-side-components">
        <div className="my-organization">
          <div className="desktop-navbar box-shadow">
            <p>{ROUTES.myOrganization.title}</p>
          </div>
          <div className="container-wrapper">
            <div className="org-details">
              <div className="img">
                <img
                  src={
                    organization?.image
                      ? `${IMAGE_URL}${organization?.image}`
                      : "/images/noImage.svg"
                  }
                  alt=""
                  aria-label={`${organization?.name || "Organization"}'s Logo`}
                />
              </div>

              <p className="title">{organization?.name}</p>
              <p>{organization?.address}</p>
              <p>{organization?.email || "No Email"}</p>
              <p>
                {organization?.phone
                  ? `${organization?.countryCode} ${organization?.phone}`
                  : "No Phone Number"}
              </p>
              <Link
                to={
                  userIsAllowedToViewOrganization
                    ? ROUTES.editOrganization.path
                    : "#"
                }
                state={
                  userIsAllowedToViewOrganization
                    ? { editData: organization }
                    : {}
                }
                id="tour-edit-organization"
                onClick={() => {
                  if (!userIsAllowedToViewOrganization) {
                    toast.dismiss();
                    toast.info(
                      "You do not have sufficient permissions to perform this action. Please contact your administrator."
                    );
                  }
                }}
              >
                <FaPen /> Edit
              </Link>
            </div>

            <div className="branch-lists">
              <div className="heading">Branches</div>
              <div className="list">
                {branches.map((el, i) => {
                  return (
                    <div
                      className={`item ${
                        selectedBranch === el ? "highlight-selected-item" : ""
                      }`}
                      key={i}
                    >
                      <div className="left">
                        <p>{el?.name}</p>
                        <span>
                          {el?.address || "No Address"} -{" "}
                          {el?.phone || "No Phone"}
                        </span>
                      </div>

                      <div className="right">
                        <FaPen
                          onClick={() => {
                            if (userIsAllowedToViewOrganization) {
                              setSelectedBranch(el);
                              setOpenUpdate(true);
                            } else {
                              toast.dismiss();
                              toast.info(
                                "You do not have sufficient permissions to perform this action. Please contact your administrator."
                              );
                            }
                          }}
                        />
                        {el?.name !== "Main branch" && (
                          <FaTrash
                            onClick={() => {
                              if (userIsAllowedToViewOrganization) {
                                setSelectedBranch(el);
                                setOpenDelete(true);
                              } else {
                                toast.dismiss();
                                toast.info(
                                  "You do not have sufficient permissions to perform this action. Please contact your administrator."
                                );
                              }
                            }}
                            className="danger"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <CustomButton
                name={"Add Branch"}
                color={`primary ${
                  currentViewPort === "mobile" || selectedBranch
                    ? ""
                    : "not-visible"
                }`}
                type={"button"}
                handleSubmit={() => {
                  setOpenUpdate(true);
                  setSelectedBranch(null);
                }}
                disabled={false}
                bigButton={true}
                id={"tour-add-branch"}
              />
            </div>
          </div>
          <DeleteModal
            deleting={deleting}
            open={openDelete}
            setOpen={setOpenDelete}
            handleDelete={handleDeleteBranch}
            name={`${selectedBranch?.name} Branch`}
          />

          <UpdateBranchModel
            open={openUpdate}
            setOpen={setOpenUpdate}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            branchId={branch_id}
            branches={branches}
            organization={organization}
          />

          {openUpdate && (
            <div
              className="blur-back"
              onClick={() => {
                setOpenUpdate(false);
                setSelectedBranch(null);
              }}
            ></div>
          )}
        </div>
        <div className="right-desktop-view"></div>
      </div>
    </>
  );
};

export default MyOrganization;

const UpdateBranchModel = ({
  open,
  setOpen,
  selectedBranch,
  setSelectedBranch,
  branchId,
  branches,
  organization,
}) => {
  const { currentViewPort } = useViewPortDetector();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);

  const clearForm = () => {
    reset({
      name: "",
      address: "",
      phone: "",
    });
  };

  useEffect(() => {
    if (open) {
      if (selectedBranch?.id) {
        reset({
          name: selectedBranch?.name,
          address: selectedBranch?.address,
          phone: selectedBranch?.phone,
        });
      } else {
        clearForm();
      }
    } else {
      clearForm();
    }
  }, [selectedBranch, open]);

  const handleSubmitBranch = (data) => {
    if (open || currentViewPort === "desktop") {
      const branchData = {
        name: data?.name,
        address: data?.address,
        phone: data?.phone,
      };
      if (selectedBranch?.id && organization?.id) {
        setSubmitting(true);
        updateBranch(organization?.id, selectedBranch?.id, branchData)
          .then((res) => {
            toast.dismiss();
            toast.success("Branch updated successfully.");
            setOpen(false);
            const newBranches = branches?.map((el) => {
              if (el?.id === selectedBranch?.id) {
                return res.data;
              } else {
                return el;
              }
            });
            storage.set("branches", JSON.stringify(newBranches));
            dispatch(updateBranches({ branches: newBranches }));

            if (selectedBranch?.id === branchId) {
              storage.set("activeBranch", JSON.stringify(res.data));
              dispatch(updateActiveBranch(res.data));
            }
            setSelectedBranch(null);
          })
          .catch((err) => {
            if (err?.response?.data?.message) {
              if (err?.response?.data?.message === "User not authorized!") {
                toast.dismiss();
                toast.error(
                  "You do not have sufficient permissions to perform this action. Please contact your administrator."
                );
              } else {
                toast.dismiss();
                toast.error(`${err?.response?.data?.message}`);
              }
            } else {
              toast.dismiss();
              toast.error("Error while updating branch!");
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      } else {
        setSubmitting(true);
        addBranch(organization?.id, branchData)
          .then((res) => {
            toast.dismiss();
            toast.success("New branch added successfully.");
            clearForm();
            storage.set("branches", JSON.stringify([...branches, res.data]));
            dispatch(updateBranches({ branches: [...branches, res.data] }));
          })
          .catch((err) => {
            if (err?.response?.data?.message) {
              if (err?.response?.data?.message === "User not authorized!") {
                toast.dismiss();
                toast.error(
                  "You do not have sufficient permissions to perform this action. Please contact your administrator."
                );
              } else {
                toast.dismiss();
                toast.error(`${err?.response?.data?.message}`);
              }
            } else {
              toast.dismiss();
              toast.error("Error while adding new branch!");
            }
            if (err?.response?.data?.upgrade) {
              dispatch(updateOpenSubscriptionAd({ openAd: true }));
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      }
    }
  };

  const [shouldRender, setShouldRender] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open || currentViewPort === "desktop") {
      setShouldRender(true);
      setTimeout(() => {
        setIsVisible(true);
      }, 10);
    } else if (isVisible) {
      setIsVisible(false);
      setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }
  }, [open, currentViewPort, isVisible]);

  if (!shouldRender) return null;
  return (
    <form
      id="tour-add-branch-desktop"
      onSubmit={handleSubmit(handleSubmitBranch)}
      className={`update-branch ${isVisible ? "active" : ""}`}
    >
      <p className="heading">
        {selectedBranch?.id ? "Edit Branch" : "Add Branch"}
      </p>
      <div className="form-content">
        <InputField
          name={"name"}
          type={"text"}
          label={"Branch Name"}
          required={true}
          register={register}
          inputErrors={errors}
          watch={watch}
        />
        <InputField
          name={"address"}
          type={"text"}
          label={"Address"}
          required={true}
          register={register}
          inputErrors={errors}
          watch={watch}
        />
        <InputField
          name={"phone"}
          type={"text"}
          label={"Phone Number"}
          required={false}
          register={register}
          constraints={{
            maxLength: {
              value: 15,
              message: "Phone number cannot exceed 15 digits",
            },
            pattern: {
              value: /^[0-9]+$/,
              message: "Only numbers are allowed",
            },
          }}
          inputErrors={errors}
          watch={watch}
        />
      </div>
      <div className="buttons">
        <CustomButton
          name={"Cancel"}
          color={"danger"}
          type={"button"}
          handleSubmit={() => {
            setOpen(false);
            setSelectedBranch(null);
            clearForm();
          }}
          disabled={submitting}
        />

        <CustomButton
          name={selectedBranch?.id ? "Edit Branch" : "Add Branch"}
          color={"primary"}
          type={"submit"}
          handleSubmit={() => {}}
          disabled={submitting}
          loader={submitting}
        />
      </div>
    </form>
  );
};
